import { Calendar, Clock, User } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface MetaTextProps {
  author?: string | null;
  date?: string | null;
  readTime?: number | null;
  className?: string;
  /** Compact mode hides author on mobile */
  compact?: boolean;
}

/**
 * MetaText - Standardized metadata display (author, date, read time)
 * 
 * Uses design tokens for consistent typography.
 * Responsive: hides author on mobile in compact mode.
 */
export function MetaText({
  author,
  date,
  readTime,
  className,
  compact = false,
}: MetaTextProps) {
  if (!author && !date && !readTime) return null;

  return (
    <div 
      className={cn(
        "flex flex-wrap items-center gap-2 md:gap-3",
        "text-[length:var(--text-sm)] text-caption",
        className
      )}
    >
      {author && (
        <span className={cn(
          "flex items-center gap-1",
          compact && "hidden sm:flex"
        )}>
          <User className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="truncate max-w-[120px] md:max-w-none">{author}</span>
        </span>
      )}
      {date && (
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" aria-hidden="true" />
          <time dateTime={date}>
            {format(new Date(date), 'MMM d, yyyy')}
          </time>
        </span>
      )}
      {readTime && (
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" aria-hidden="true" />
          {readTime} min
        </span>
      )}
    </div>
  );
}
